import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Input.css';

/**
 * Input Component
 * Shared input component for consistent styling across web and future mobile platforms
 *
 * Features:
 * - Text, email, number, password, and other HTML5 input types
 * - Error states with visual feedback
 * - Disabled states
 * - Character counter (optional)
 * - Full accessibility support
 */
const Input = forwardRef(
  (
    {
      type = 'text',
      value,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      disabled = false,
      readOnly = false,
      required = false,
      autoFocus = false,
      maxLength,
      min,
      max,
      step,
      error,
      showCharCount = false,
      helperText,
      className = '',
      ariaLabel,
      ariaDescribedBy,
      id,
      name,
      ...rest
    },
    ref
  ) => {
    const inputClasses = [
      'shared-input',
      error && 'input-error',
      disabled && 'input-disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const showCounter = showCharCount && maxLength;
    const charCount = value ? value.toString().length : 0;

    // Support both controlled and uncontrolled modes
    const inputProps = {
      ref,
      type,
      id,
      name,
      className: inputClasses,
      onBlur,
      onFocus,
      placeholder,
      disabled,
      readOnly,
      required,
      autoFocus,
      maxLength,
      min,
      max,
      step,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': !!error,
      ...rest,
    };

    // Only add value/onChange for controlled inputs
    if (value !== undefined) {
      inputProps.value = value;
    }
    if (onChange !== undefined) {
      inputProps.onChange = onChange;
    }

    return (
      <div className="input-wrapper">
        <input {...inputProps} />
        {showCounter && (
          <div className="char-counter" aria-live="polite" aria-atomic="true">
            <span className={charCount >= maxLength ? 'char-limit-reached' : ''}>
              {charCount}/{maxLength}
            </span>
          </div>
        )}
        {error && (
          <div className="input-error-message" role="alert">
            {error}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'email',
    'password',
    'number',
    'tel',
    'url',
    'search',
    'date',
    'time',
    'datetime-local',
  ]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  autoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  showCharCount: PropTypes.bool,
  helperText: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Input;
